// ═══════════════════════════════════════════════════════════════
// AnnieDashboard — Dashboard Personnel Collaboratrice (données réelles)
// Session 33 — Rewrite complet (ex-hardcodé)
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { Users, FolderOpen, MessageSquare, FileText, RefreshCw } from 'lucide-react';
import ChroniclesRibbon from '../greg/ChroniclesRibbon';

const AnnieDashboard = ({ onProjectSelect }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadAll = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const token = localStorage.getItem('oracle_token');
      const res = await fetch('/api/dashboard/annie', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const json = await res.json();
        setData(json.data);
      }
    } catch (e) {
      console.error('Erreur dashboard:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>Chargement dashboard...</div>;

  const cardStyle = {
    background: 'var(--card-bg)', border: '1px solid var(--card-border)',
    borderRadius: '12px', padding: '20px',
  };

  const statBox = (icon, label, value, color) => (
    <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: '14px' }}>
      <div style={{
        width: '42px', height: '42px', borderRadius: '10px',
        background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {React.cloneElement(icon, { size: 20, style: { color } })}
      </div>
      <div>
        <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '2px' }}>{label}</div>
        <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text)' }}>{value}</div>
      </div>
    </div>
  );

  const projects = data?.projects || [];
  const conversations = data?.recent_conversations || [];
  const files = data?.files || { total_uploaded: 0, recent: [] };
  const collab = data?.collaboration || { projects_active: 0, total_conversations: 0 };
  const sharedCount = projects.filter(p => p.is_shared).length;

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  return (
    <div style={{ padding: '24px', height: '100%', overflow: 'auto' }}>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Users size={22} style={{ color: '#8b5cf6' }} />
            Dashboard Collaboration
          </h2>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Vue d'ensemble personnelle
          </p>
        </div>
        <button onClick={() => loadAll(true)} style={{
          padding: '8px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '500',
          cursor: 'pointer', border: 'none', display: 'flex', alignItems: 'center', gap: '6px',
          background: 'linear-gradient(135deg, #7c3aed, #6d28d9)', color: 'white',
        }}>
          <RefreshCw size={13} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
        </button>
      </div>

      {/* Chroniques — journal éditorial ITRIIA (silent si rien à publier pour cet user) */}
      <ChroniclesRibbon limit={4} />

      {/* Stats cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        {statBox(<FolderOpen />, 'Projets actifs', String(collab.projects_active), '#3b82f6')}
        {statBox(<Users />, 'Projets partages', String(sharedCount), '#8b5cf6')}
        {statBox(<MessageSquare />, 'Conversations', String(collab.total_conversations), '#f59e0b')}
        {statBox(<FileText />, 'Fichiers uploades', String(files.total_uploaded), '#10b981')}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
        {/* Projets */}
        <div style={cardStyle}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FolderOpen size={16} style={{ color: '#3b82f6' }} />
            Mes Projets
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {projects.length === 0 && (
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Aucun projet</div>
            )}
            {projects.slice(0, 5).map(p => (
              <div key={p.id} onClick={() => onProjectSelect && onProjectSelect(p)} style={{
                padding: '10px', borderRadius: '8px', background: 'var(--input-bg)',
                cursor: onProjectSelect ? 'pointer' : 'default', transition: 'background 0.15s',
              }}
                onMouseEnter={e => onProjectSelect && (e.currentTarget.style.background = 'rgba(59,130,246,0.1)')}
                onMouseLeave={e => onProjectSelect && (e.currentTarget.style.background = 'var(--input-bg)')}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text)' }}>{p.name}</span>
                  {p.is_shared && (
                    <span style={{
                      fontSize: '10px', fontWeight: '600', padding: '1px 8px', borderRadius: '8px',
                      background: 'rgba(139,92,246,0.1)', color: '#8b5cf6',
                    }}>
                      Invite
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: 'var(--text-secondary)' }}>
                  <span>{p.conversation_count} conv.</span>
                  <span>{p.file_count} fichiers</span>
                  <span>{p.participant_count} membres</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Conversations récentes */}
        <div style={cardStyle}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <MessageSquare size={16} style={{ color: '#f59e0b' }} />
            Conversations recentes
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {conversations.length === 0 && (
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Aucune conversation</div>
            )}
            {conversations.map(c => (
              <div key={c.id} style={{ padding: '10px', borderRadius: '8px', background: 'var(--input-bg)' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text)', marginBottom: '4px' }}>
                  {c.title || 'Sans titre'}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--text-secondary)' }}>
                  <span>{c.project_name}</span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <span>{c.message_count} msg</span>
                    <span>{formatDate(c.created_at)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Fichiers récents */}
      {files.recent && files.recent.length > 0 && (
        <div style={cardStyle}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FileText size={16} style={{ color: '#10b981' }} />
            Fichiers recents ({files.total_uploaded} total)
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' }}>
            {files.recent.map((f, i) => (
              <div key={i} style={{
                padding: '10px', borderRadius: '8px', background: 'var(--input-bg)',
                border: '1px solid var(--card-border)',
              }}>
                <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text)', marginBottom: '4px' }}>
                  {f.original_name}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--text-secondary)' }}>
                  <span>{f.project_name}</span>
                  <span>{formatDate(f.uploaded_at || f.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnieDashboard;
